import React, { useState, useEffect, useCallback, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { Box, Button, LinearProgress, TextField, Typography } from "@mui/material";
import { Warning as WarningIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import {
  refreshSelectedAccountBalance,
  updateSelectedAccount,
  updateCurrentAccountInDB,
  getWinterCrisisHistoryForAccount,
  getMedFormHistoryForAccount,
  checkWinterCrisisAvailability,
  getAccountBalance,
} from "modules/account/api/actions";
import { store } from "../../../setup/store";
import { hasActiveItemInRange } from "utils/Utils";
import {
  getUserPayments,
  getAccountPaymentPlans,
} from "modules/payments/api/actions";
import { setSelectedUserProfile } from "modules/userProfiles/api/actions";
import { DIALOGS } from "../hooks/useDialogManager";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Lazy-loaded dialog content components
const AccountLedgerLogTable = React.lazy(() => import("modules/account/components/AccountLedgerLogTable"));
const AccountDisconnectsTable = React.lazy(() => import("modules/accountDisconnects/components/Table"));
const UnitWorkOrdersTable = React.lazy(() => import("modules/workOrders/components/UnitWorkOrderTable"));
const SelectedAccountMeterCard = React.lazy(() => import("modules/account/components/AccountPropInfo/SelectedAccountMeterCard"));
const AccountEmailPopupEditor = React.lazy(() => import("modules/notification/components/accountEmailPopup"));
const AccountFilesTable = React.lazy(() => import("modules/files/components/AccountFilesTable"));
const ArReceiptsTable = React.lazy(() => import("modules/arReceipts/components/AccountArReceiptsTable"));
const UserDetailSummaryCard = React.lazy(() => import("modules/userProfiles/components/UserDetailSummaryCard"));
const UserPaymentMethodTypesTable = React.lazy(() => import("modules/userProfiles/components/UserPaymentMethodTypesTable"));
const BillForm = React.lazy(() => import("modules/bills/components/BillForm"));
const PaymentForm = React.lazy(() => import("modules/payments/components/PaymentForm"));
const AccountNewMoveOutForm = React.lazy(() => import("modules/moves/components/AccountNewMoveOutForm"));
const AccountForm = React.lazy(() => import("modules/account/components/AccountForm"));
const TransferUnitForm = React.lazy(() => import("modules/moves/components/TransferUnitForm"));
const AccountPaymentPlanTable = React.lazy(() => import("modules/account/components/AccountPaymentPlanTable"));
const AutoPaymentForm = React.lazy(() => import("modules/payments/components/AutoPaymentForm"));
const ForwardingAddressForm = React.lazy(() => import("modules/account/components/ForwardingAddressForm"));
const PledgeAccountForm = React.lazy(() => import("modules/account/components/PledgeAccountTable"));
const WinterCrisisHistoryTable = React.lazy(() => import("modules/account/components/WinterCrisisHistoryTable"));
const MedFormHistoryTable = React.lazy(() => import("modules/account/components/MedFormHistoryTable"));
const AccountComplaintsTable = React.lazy(() => import("modules/complaints/components/AccountComplaintsTable"));

const SuspenseFallback = () => <LinearProgress sx={{ m: 2 }} />;

function DialogManager({ activeDialog, closeDialog, currentAccountUuid, dialogData }) {
  const dispatch = useDispatch();
  const params = useParams();
  const account = useSelector((state) => state.account);
  const bills = useSelector((state) => state.bills);
  const setting = useSelector((state) => state.setting);

  const selectedMeterId = dialogData?.selectedMeterId ?? null;
  const [wasLoadingWinterCrisis, setWasLoadingWinterCrisis] = useState(false);

  // Winter crisis loading tracking
  useEffect(() => {
    const isWCOpen = activeDialog === DIALOGS.WINTER_CRISIS_HISTORY;
    if (isWCOpen && account.isLoading) {
      setWasLoadingWinterCrisis(true);
    }
    if (isWCOpen && wasLoadingWinterCrisis && !account.isLoading && account.winterCrisisHistory) {
      setWasLoadingWinterCrisis(false);
      const itemCount = account.winterCrisisHistory.items?.length || 0;
      const message = itemCount > 0
        ? `Winter crisis history loaded successfully (${itemCount} ${itemCount === 1 ? "entry" : "entries"})`
        : "Winter crisis history loaded (no entries found)";
      dispatch(setSnackbar(true, "success", message));
    }
  }, [activeDialog, account.isLoading, account.winterCrisisHistory?.items, wasLoadingWinterCrisis, dispatch]);

  useEffect(() => {
    if (activeDialog !== DIALOGS.WINTER_CRISIS_HISTORY) {
      setWasLoadingWinterCrisis(false);
    }
  }, [activeDialog]);

  const getMinimumWinterCrisisCoverAmount = useCallback(() => {
    if (setting?.settings?.items?.length > 0) {
      const s = setting.settings.items.find((x) => x.name === "winterCrisisCoverAmount");
      if (s?.value) return Number(parseFloat(s.value).toFixed(2));
    }
    return 211.0;
  }, [setting?.settings?.items]);

  const handlePaymentSuccess = useCallback(
    (paymentResponse, paymentAmount) => {
      const hasSuccessResponse =
        paymentResponse &&
        ((paymentResponse.data && paymentResponse.data.items && paymentResponse.data.transactionResult) ||
          (paymentResponse.items && paymentResponse.transactionResult));

      if (hasSuccessResponse && paymentAmount) {
        dispatch(setSnackbar(true, "warning", "Updating payment records..."));

        const accountUuid = account.data.account?.uuid || account.initialAccount?.uuid;
        if (accountUuid) dispatch(getUserPayments(accountUuid));

        const currentBalance = account.data.ongoingAccountBalance;
        if (currentBalance?.id) {
          const updatedBalance = {
            ...currentBalance,
            balance: parseFloat(Math.max(0, (currentBalance.balance || 0) - paymentAmount).toFixed(2)),
            pastDue: parseFloat(Math.max(0, (currentBalance.pastDue || 0) - paymentAmount).toFixed(2)),
          };
          dispatch(refreshSelectedAccountBalance(updatedBalance));
        }

        setTimeout(() => {
          dispatch(setSnackbar(false, "success", ""));
        }, 1000);
      }
    },
    [account.data, account.initialAccount, dispatch]
  );

  const handleBillFormUpdate = useCallback(() => {
    dispatch(setSnackbar(false, "success", ""));
    setTimeout(() => closeDialog(), 100);
  }, [closeDialog, dispatch]);

  const handlePaymentFormUpdate = useCallback(
    (paymentResponse, paymentAmount) => {
      handlePaymentSuccess(paymentResponse, paymentAmount);
      closeDialog();
    },
    [handlePaymentSuccess, closeDialog]
  );

  const handleMedFormHistoryClose = useCallback(() => {
    closeDialog();
    const accountNum = account.data.account?.accountNum;
    if (!accountNum) {
      return;
    }
    Promise.resolve(dispatch(getMedFormHistoryForAccount(accountNum)))
      .then(() => {
        const items = store.getState().account.medFormHistory?.items || [];
        if (!hasActiveItemInRange(items) && account.data.account?.medForm) {
          const updatedAccount = { ...account.data.account, medForm: false };
          const updatedData = { ...account.data, account: updatedAccount };
          dispatch(updateSelectedAccount(updatedData));
          dispatch(updateCurrentAccountInDB({ uuid: account.data.account.uuid, medForm: false }))
            .then(() => console.log("Account medForm status set to false"))
            .catch((error) => console.error("Error updating account medForm status:", error));
        }
      })
      .catch((error) => console.error("Error refreshing med form history:", error));
  }, [closeDialog, account.data, dispatch]);

  const handlePaymentPlanClose = useCallback(() => {
    closeDialog();
    if (account.data.account?.uuid) {
      dispatch(getAccountPaymentPlans(account.data.account.uuid));
    }
  }, [closeDialog, account.data.account?.uuid, dispatch]);

  const onPaymentPlanCreated = useCallback(() => {
    if (account.data.account?.uuid) {
      dispatch(getAccountPaymentPlans(account.data.account.uuid));
    }
    const updatedAccount = { ...account.data.account, paymentPlan: true };
    const updatedData = { ...account.data, account: updatedAccount };
    dispatch(updateSelectedAccount(updatedData));
    dispatch(updateCurrentAccountInDB({ uuid: account.data.account.uuid, paymentPlan: true }))
      .then(() => dispatch(setSnackbar(true, "success", "Payment Plan created and account updated successfully")))
      .catch(() => dispatch(setSnackbar(true, "error", "Payment Plan created but account update failed")));
    setTimeout(() => closeDialog(), 500);
  }, [closeDialog, account.data, dispatch]);

  const handleWinterCrisisPaymentUpdate = useCallback(
    async (paymentResponse, paymentAmount) => {
      handlePaymentSuccess(paymentResponse, paymentAmount);
      if (account.data.account?.accountNum) {
        dispatch(checkWinterCrisisAvailability(account.data.account.accountNum));
        dispatch(getWinterCrisisHistoryForAccount(account.data.account.accountNum));
      }
      const updatedAccount = { ...account.data.account, winterCrisis: true };
      const updatedData = { ...account.data, account: updatedAccount };
      dispatch(updateSelectedAccount(updatedData));
      dispatch(updateCurrentAccountInDB(updatedAccount))
        .then(() => dispatch(setSnackbar(true, "success", "Winter Crisis activated successfully")))
        .catch(() => dispatch(setSnackbar(true, "error", "Error activating Winter Crisis")));
      closeDialog();
    },
    [handlePaymentSuccess, account.data, dispatch, closeDialog]
  );

  const handleUserProfileClose = useCallback(() => {
    dispatch(setSelectedUserProfile(null));
    closeDialog();
  }, [closeDialog, dispatch]);

  const handleWinterCrisisHistoryOpen = useCallback(() => {
    if (account.data.account?.accountNum) {
      if (!account.data.ongoingAccountBalance?.balance && account.data.account?.uuid) {
        dispatch(getAccountBalance(account.data.account.uuid));
      }
      dispatch(getWinterCrisisHistoryForAccount(account.data.account.accountNum));
      dispatch(setSnackbar(true, "info", "Loading winter crisis history..."));
    }
  }, [account.data, dispatch]);

  // Trigger data load when winter crisis history dialog opens
  useEffect(() => {
    if (activeDialog === DIALOGS.WINTER_CRISIS_HISTORY) {
      handleWinterCrisisHistoryOpen();
    }
  }, [activeDialog]);

  const accountNum = account.data.account?.accountNum;
  const isActive = (name) => activeDialog === name;

  return (
    <>
      {/* Ledger */}
      {isActive(DIALOGS.LEDGER) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><AccountLedgerLogTable /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Disconnects */}
      {isActive(DIALOGS.DISCONNECTS) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <AccountDisconnectsTable accountNum={params.acctNum} isAccountPage={true} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Work Orders */}
      {isActive(DIALOGS.WORK_ORDERS) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <UnitWorkOrdersTable unitWorkOrderTablesIsOpen={true} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Meter Readings */}
      {isActive(DIALOGS.METER_READINGS) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <SelectedAccountMeterCard callbackModal={closeDialog} isOpen={true} selectedMeter={selectedMeterId} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Contact Account Holder */}
      {isActive(DIALOGS.CONTACT_ACCOUNT_HOLDER) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="lg" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <AccountEmailPopupEditor onCloseFormMethod={closeDialog} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Account Files */}
      {isActive(DIALOGS.ACCOUNT_FILES) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><AccountFilesTable /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* AR Receipts */}
      {isActive(DIALOGS.AR_RECEIPTS) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="lg" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><ArReceiptsTable /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* User Profile */}
      {isActive(DIALOGS.USER_PROFILE) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="lg" onClose={handleUserProfileClose}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                <Box sx={{ flex: "1 1 100%" }}><UserDetailSummaryCard /></Box>
                <Box sx={{ flex: "1 1 100%" }}>
                  <UserPaymentMethodTypesTable accountUuid={currentAccountUuid} />
                </Box>
              </Box>
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Bill Form */}
      {isActive(DIALOGS.BILL_FORM) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="lg" onClose={closeDialog}>
          <DialogTitle>
            {accountNum ? `New Bill for Account #${accountNum}` : "New Bill for Account: LOADING....."}
          </DialogTitle>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <BillForm onUpdate={handleBillFormUpdate} onCancel={closeDialog} accountNum={accountNum || -1} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Payment Form */}
      {isActive(DIALOGS.PAYMENT_FORM) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="lg" onClose={closeDialog}>
          <DialogTitle>
            {accountNum ? `New Payment for Account #${accountNum}` : "New Payment for Account: LOADING....."}
          </DialogTitle>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <PaymentForm isOpen={true} onUpdate={handlePaymentFormUpdate} onCancel={closeDialog} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Move Out Form */}
      {isActive(DIALOGS.MOVE_OUT) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <AccountNewMoveOutForm onClose={closeDialog} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Account Form */}
      {isActive(DIALOGS.EDIT_ACCOUNT) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="lg" onClose={closeDialog}>
          <DialogTitle>
            {accountNum ? `Edit Account #${accountNum}` : "Edit Account: LOADING....."}
          </DialogTitle>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <AccountForm onUpdate={closeDialog} onCancel={closeDialog} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Transfer Unit Form */}
      {isActive(DIALOGS.TRANSFER_UNIT) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogTitle>
            Change Account Unit{accountNum ? ` - #${accountNum}` : ""}
          </DialogTitle>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <TransferUnitForm isOpen={true} onClose={closeDialog} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Payment Plan */}
      {isActive(DIALOGS.PAYMENT_PLAN) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={handlePaymentPlanClose}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <AccountPaymentPlanTable isAutoPayForm={true} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Auto-Payment Form */}
      {isActive(DIALOGS.AUTO_PAY) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><AutoPaymentForm /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Forwarding Address Form */}
      {isActive(DIALOGS.FORWARDING_ADDRESS) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><ForwardingAddressForm /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Pledge Account Form */}
      {isActive(DIALOGS.PLEDGE_ACCOUNT) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><PledgeAccountForm /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Winter Crisis History */}
      {isActive(DIALOGS.WINTER_CRISIS_HISTORY) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogTitle>Winter Crisis History</DialogTitle>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <WinterCrisisHistoryTable
                account={account}
                key={`winter-crisis-${account.data.account?.accountNum || "loading"}`}
              />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Winter Crisis Payment Form */}
      {isActive(DIALOGS.WINTER_CRISIS_PAYMENT) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <PaymentForm
                initialPaymentAmount={getMinimumWinterCrisisCoverAmount()}
                onUpdate={handleWinterCrisisPaymentUpdate}
                onCancel={closeDialog}
              />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Med Form History */}
      {isActive(DIALOGS.MED_FORM_HISTORY) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={handleMedFormHistoryClose}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}><MedFormHistoryTable /></Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Complaints */}
      {isActive(DIALOGS.COMPLAINTS) && (
        <Dialog open TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={closeDialog}>
          <DialogContent>
            <Suspense fallback={<SuspenseFallback />}>
              <AccountComplaintsTable accountUuid={currentAccountUuid} isOpen={true} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/* Payment Blocks */}
      {isActive(DIALOGS.PAYMENT_BLOCKS) && (
        <PaymentBlocksDialog account={account} bills={bills} dispatch={dispatch} closeDialog={closeDialog} />
      )}
    </>
  );
}

function PaymentBlocksDialog({ account, bills, dispatch, closeDialog }) {
  const acct = account.data?.account;
  const timesLate = bills.accountBillingTimesLate ?? 0;
  const timesLateLoading = bills.accountBillingTimesLateIsLoading;

  const [badCards, setBadCards] = React.useState(Number(acct?.badCards) || 0);
  const [badChecks, setBadChecks] = React.useState(Number(acct?.badChecks) || 0);
  const [saving, setSaving] = React.useState(false);

  const hasChanges = badCards !== (Number(acct?.badCards) || 0) || badChecks !== (Number(acct?.badChecks) || 0);

  const handleSave = useCallback(() => {
    const updatedAccount = { ...acct, badCards, badChecks };
    const updatedData = { ...account.data, account: updatedAccount };
    setSaving(true);
    dispatch(updateSelectedAccount(updatedData));
    dispatch(setSnackbar(true, "warning", "Saving Account Detail Changes"));
    dispatch(updateCurrentAccountInDB(updatedAccount)).then(() => {
      setSaving(false);
      dispatch(setSnackbar(true, "success", "Saving Successful"));
      closeDialog();
    });
  }, [acct, account.data, badCards, badChecks, dispatch, closeDialog]);

  const getFieldSx = (isBlocking) => ({
    width: 64,
    "& .MuiOutlinedInput-root": {
      height: 32,
      fontSize: "0.875rem",
      fontWeight: 600,
      ...(isBlocking && { "& fieldset": { borderColor: "error.main" } }),
    },
    "& input": { textAlign: "center", p: "4px 8px", color: isBlocking ? "error.main" : "text.primary" },
  });

  return (
    <Dialog open TransitionComponent={Transition} maxWidth="xs" fullWidth onClose={closeDialog}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 0.5, fontSize: "1rem" }}>
        <WarningIcon fontSize="small" color={(badCards >= 3 || badChecks >= 3) ? "error" : "action"} />
        Bad Cards & Checks
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Card and check payments are blocked when count reaches 3.
        </Typography>

        <Box display="flex" flexDirection="column" gap={1.5}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <ScheduleIcon sx={{ fontSize: "1rem" }} color={timesLate >= 3 ? "warning" : "action"} />
              <Typography variant="body2" fontWeight="500">Times Late</Typography>
            </Box>
            <Typography variant="body2" fontWeight="700" color={timesLate >= 3 ? "warning.main" : "text.primary"}>
              {timesLateLoading ? "..." : timesLate}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2" fontWeight="500" color={badCards >= 3 ? "error.main" : "text.primary"}>
                Bad Cards
              </Typography>
              {badCards >= 3 && (
                <Typography variant="caption" color="error.main" sx={{ fontSize: "0.65rem" }}>Credit/debit cards blocked</Typography>
              )}
            </Box>
            <TextField
              type="number"
              size="small"
              value={badCards}
              onChange={(e) => setBadCards(Math.max(0, Number(e.target.value) || 0))}
              inputProps={{ min: 0 }}
              sx={getFieldSx(badCards >= 3)}
            />
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2" fontWeight="500" color={badChecks >= 3 ? "error.main" : "text.primary"}>
                Bad Checks
              </Typography>
              {badChecks >= 3 && (
                <Typography variant="caption" color="error.main" sx={{ fontSize: "0.65rem" }}>eCheck/ACH payments blocked</Typography>
              )}
            </Box>
            <TextField
              type="number"
              size="small"
              value={badChecks}
              onChange={(e) => setBadChecks(Math.max(0, Number(e.target.value) || 0))}
              inputProps={{ min: 0 }}
              sx={getFieldSx(badChecks >= 3)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={closeDialog} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasChanges || saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogManager;
